import { ComponentType, ReactNode } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { AUTH_SESSIONS_LIST_IN_UI, useVariableValue } from "lib/featureFlags";
import ActiveSessionsSection from "./ActiveSessionsSection";
import ChangeEmail from "./ChangeEmail";
import ChangeName from "./ChangeName";
import ChangePassword from "./ChangePassword";
import ConnectedAccountsSection from "./ConnectedAccountsSection";
import SettingsSection from "./SettingsSection";

type AccountSection = {
  key: string;
  title: string;
  description?: ReactNode;
  enabled: boolean;
  Component: ComponentType;
};

/** The signed-in user's own account: how they're identified and how they sign in. */
function AccountSettingsPage(): JSX.Element {
  // Revoking a session doesn't end the app's own session cookie yet, so keep this off until it does.
  const isSessionsListEnabled = useVariableValue(AUTH_SESSIONS_LIST_IN_UI, false);

  // Rendered in order. Moving another settings page in here (e.g. Security) is one more entry.
  const sections: AccountSection[] = [
    { key: "name", title: "Name", enabled: true, Component: ChangeName },
    { key: "email", title: "Email", enabled: true, Component: ChangeEmail },
    { key: "password", title: "Password", enabled: true, Component: ChangePassword },
    {
      key: "sign-in-methods",
      title: "Sign-in methods",
      description: "The ways you can sign in to Stotles.",
      enabled: true,
      Component: ConnectedAccountsSection,
    },
    {
      key: "sessions",
      title: "Active sessions",
      description: "Devices that are signed in to your account.",
      enabled: isSessionsListEnabled,
      Component: ActiveSessionsSection,
    },
  ];

  return (
    <Page>
      <Flex column gap={16}>
        <Text h1>Account</Text>
        {sections
          .filter(({ enabled }) => enabled)
          .map(({ key, title, description, Component }) => (
            <SettingsSection key={key} title={title} description={description}>
              <Component />
            </SettingsSection>
          ))}
      </Flex>
    </Page>
  );
}

export default AccountSettingsPage;

const Page = styled.div(({ theme }) => ({
  height: "100%",
  overflow: "auto",
  padding: 24,
  backgroundColor: theme.colors.sysBackgroundDefault,
}));
